import { useMemo, useState, useCallback } from 'react';
import { SortType, SortDirection } from '@css-xp/graphtypes/src/generated-types-new';

export type SortKeyType =
  | 'id'
  | 'createdDate'
  | 'expiryDate'
  | 'estimatesTotalAmount'
  | 'updatedDate'
  | 'shipToDisplayName'
  | 'status';

export const SORT_KEYS: Record<string, SortKeyType> = {
  ID: 'id',
  CREATED_DATE: 'createdDate',
  EXPIRY_DATE: 'expiryDate',
  TOTAL_AMOUNT: 'estimatesTotalAmount',
  UPDATED_DATE: 'updatedDate',
  COMPANY_NAME: 'shipToDisplayName',
  STATUS: 'status',
};

export const DEFAULT_SORT_KEY = SORT_KEYS.CREATED_DATE;

const getIsAscendingDefaultValue = (sortKey: SortKeyType): boolean => {
  return sortKey !== SORT_KEYS.CREATED_DATE;
};

const getSortOptions = ({
  currentSortKey,
  isAscending,
}: {
  currentSortKey: SortKeyType;
  isAscending: boolean;
}): SortType => {
  return {
    sortFields: [currentSortKey, SORT_KEYS.ID],
    sortFieldsDirections: [isAscending ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

export const useQuoteTableSort = () => {
  const [currentSortKey, setCurrentSortKey] = useState<SortKeyType>(DEFAULT_SORT_KEY);
  const [isAscending, setIsAscending] = useState<boolean>(getIsAscendingDefaultValue(DEFAULT_SORT_KEY));

  const onSortKeyChange = useCallback(
    (sortKey: SortKeyType) => {
      if (sortKey === currentSortKey) {
        const newIsAscending = !isAscending;
        setIsAscending(newIsAscending);
        return getSortOptions({ currentSortKey, isAscending: newIsAscending });
      } else {
        setCurrentSortKey(sortKey);
        setIsAscending(getIsAscendingDefaultValue(sortKey));
        return getSortOptions({ currentSortKey: sortKey, isAscending: getIsAscendingDefaultValue(sortKey) });
      }
    },
    [currentSortKey, isAscending]
  );

  const sortOptions = useMemo((): SortType => {
    return getSortOptions({ currentSortKey, isAscending });
  }, [currentSortKey, isAscending]);

  const resetSortOptions = useCallback(() => {
    setCurrentSortKey(DEFAULT_SORT_KEY);
    setIsAscending(getIsAscendingDefaultValue(DEFAULT_SORT_KEY));
    return getSortOptions({
      currentSortKey: DEFAULT_SORT_KEY,
      isAscending: getIsAscendingDefaultValue(DEFAULT_SORT_KEY),
    });
  }, []);

  return { currentSortKey, isAscending, sortOptions, onSortKeyChange, resetSortOptions };
};
